import { inject, Injectable } from '@angular/core';
import { ApplicationTypeEnum } from '../../../../../core/constants/application-type-enum';
import { RouteConstants } from '../../../../../core/constants/routes';
import { UserRole } from '../../../../../core/enums/users/role';
import { ApplicationState } from '../../../../../core/models/application-state/application-state';
import { UserStateService } from '../../../../user/shared/services/user-state/user-state.service';
import { DirectEntryStepEnum } from '../../../direct-entry/shared/constants/direct-entry-steps';
import {
    ApplicationFilingStatus800MHzHoldPattern,
    ApplicationFilingStatusEnum,
} from '../../constants/application-filing-status';
import { PublicSafetyCoordinatorCodeEnum } from '../../constants/public-safety-coordinator-codes';
import { StepLink } from '../../models/step-link';
import { ApplicationFormControlManagerService } from '../application-form-control-manager/application-form-control-manager.service';
import { PublicSafetyApplicationPurposeCodes } from '../../constants/application-purpose-codes';

@Injectable({
    providedIn: 'root',
})
export class StepNavigationButtonVisibilityService {
    private readonly userStateService: UserStateService =
        inject(UserStateService);
    private readonly applicationFormControlManagerService: ApplicationFormControlManagerService =
        inject(ApplicationFormControlManagerService);

    /**
     * If the current step needs the next button displayed.
     * @param stepLink Current step link.
     * @returns Boolean value indicating if the next button should be displayed.
     */
    needsNextButton(stepLink: StepLink): boolean {
        if (
            RouteConstants.Application.isDirectEntryRoute(stepLink.url) &&
            stepLink.id === DirectEntryStepEnum.SubmitToEwa
        ) {
            // This is the application's final page. This page replaces the standard "Next" button with a dedicated submission button.
            return false;
        } else if (
            RouteConstants.Application.isCoordinationResultsRoute(stepLink.url)
        ) {
            return false;
        } else if (RouteConstants.Go.isSearchProcessingRoute(stepLink.url)) {
            return false;
        } else if (
            RouteConstants.Application.isUnsupportedApplicationRoute(
                stepLink.url,
            )
        ) {
            return false;
        } else if (
            RouteConstants.FrnFiling.isFrnFilingDetailRoute(stepLink.url)
        ) {
            return false;
        }

        return true;
    }

    /**
     * Is beyond the first page of a wizard, direct entry, or template application.
     * @param stepLink Current step link.
     * @returns Boolean value indicating if the previous button should be displayed.
     */
    needsPreviousButton(stepLink: StepLink): boolean {
        if (RouteConstants.Application.isNewApplicationRoute(stepLink.url)) {
            return false;
        }

        return stepLink.id > 1;
    }

    /**
     * If the current step needs the save and close button displayed.
     *
     * @param stepLink Current step link.
     * @param isHandedOverToEwa Optional. Boolean value indicating if the application has already been handed over to EWA.
     * @returns Boolean value indicating if the save and close button should be displayed.
     */
    needsSaveAndCloseButton(
        stepLink: StepLink,
        isHandedOverToEwa?: boolean,
    ): boolean {
        const isDirectEntry = RouteConstants.Application.isDirectEntryRoute(
            stepLink.url,
        );

        const isFrnExistingApplication =
            RouteConstants.FrnFiling.isExistingFrnFilingRoute(stepLink.url);

        const isUnsupportedApplication =
            RouteConstants.Application.isUnsupportedApplicationRoute(
                stepLink.url,
            );

        const isCoordination =
            RouteConstants.Application.isCoordinationApplicationRoute(
                stepLink.url,
            );

        const isReadOnly =
            this.applicationFormControlManagerService.isReadOnly();

        return (
            (isCoordination && !isReadOnly) ||
            (isDirectEntry && !isReadOnly) ||
            (isUnsupportedApplication && !isHandedOverToEwa) ||
            (isFrnExistingApplication && !isReadOnly)
        );
    }

    /**
     * If the current step needs the handover to EWA button displayed.
     *
     * @param stepLink Current step link.
     * @param isApplicationTemplate Boolean value indicating if the application is a template.
     * @param isHandedOverToEwa Boolean value indicating if the application has already been handed over to EWA.
     * @returns Boolean value indicating if the handover to EWA button should be displayed.
     */
    needsHandoverToEwaButton(
        stepLink: StepLink,
        isApplicationTemplate: boolean,
        isHandedOverToEwa: boolean,
    ): boolean {
        if (this.applicationFormControlManagerService.isReadOnly()) {
            return false;
        }

        const isDirectEntry = RouteConstants.Application.isDirectEntryRoute(
            stepLink.url,
        );

        const isFrnExistingApplication =
            RouteConstants.FrnFiling.isExistingFrnFilingRoute(stepLink.url);

        const isCoordinationResults =
            RouteConstants.Application.isCoordinationResultsRoute(stepLink.url);

        const isCustomerRole =
            this.userStateService.getRole() === UserRole.Customer;

        return (
            (isDirectEntry &&
                isCustomerRole &&
                !isApplicationTemplate &&
                !isHandedOverToEwa) ||
            (isCoordinationResults && isCustomerRole && !isHandedOverToEwa) ||
            (isFrnExistingApplication && !isHandedOverToEwa)
        );
    }

    /**
     * If the current step needs the Submit Menu displayed.
     * @param stepLink Current step link.
     * @param isApplicationTemplate Boolean value indicating if the application is a template.
     * @returns Boolean value indicating if the submit menu should be displayed.
     */
    needsSubmitMenu(
        stepLink: StepLink,
        isApplicationTemplate: boolean,
    ): boolean {
        return (
            RouteConstants.Application.isDirectEntryRoute(stepLink.url) &&
            this.userStateService.getRole() !== UserRole.Customer &&
            !isApplicationTemplate
        );
    }

    /**
     * If the current step needs the Tools menu displayed.
     *
     * @param stepLink Current step link.
     * @param isApplicationTemplate Boolean value indicating if the application is a template.
     * @returns Boolean value indicating if the tools menu should be displayed.
     */
    needsToolsMenu(
        stepLink: StepLink,
        isApplicationTemplate: boolean,
    ): boolean {
        return (
            RouteConstants.Application.isDirectEntryRoute(stepLink.url) &&
            !isApplicationTemplate
        );
    }

    /**
     * If the current step needs the Submit for Coordination button displayed.
     *
     * @param stepLink Current step link.
     * @returns Boolean value indicating if the submit for Coordination button should be displayed.
     */
    needsSubmitForCoordinationButton(stepLink: StepLink): boolean {
        const isCoordinationResultRoute =
            RouteConstants.Application.isCoordinationResultsRoute(stepLink.url);

        return (
            isCoordinationResultRoute &&
            !this.applicationFormControlManagerService.isReadOnly()
        );
    }

    /**
     * If the current Coordination step needs the Submit to FCC button displayed.
     * @param stepLink Current step link.
     * @returns Boolean value indicating if the submit to FCC button should be displayed.
     */
    needsCoordinationSubmitToFccButton(stepLink: StepLink): boolean {
        return (
            RouteConstants.Application.isCoordinationResultsRoute(
                stepLink.url,
            ) && this.userStateService.getRole() !== UserRole.Customer
        );
    }

    /**
     * If the Submit to FCC menu should be enabled.
     * @returns Boolean value indicating if the submit to FCC menu should be enabled.
     */
    isSubmitToFccMenuEnabled(filingStatus?: string | null): boolean {
        return (
            filingStatus != null &&
            (filingStatus.startsWith(
                ApplicationFilingStatusEnum.entryComplete,
            ) ||
                filingStatus.startsWith(
                    ApplicationFilingStatusEnum.submittedToEwa,
                ) ||
                filingStatus ===
                    ApplicationFilingStatusEnum.coordinationResultsReady)
        );
    }

    /**
     * If the current step needs the Submit to PSCA button displayed.
     * @param stepLink Current step link.
     * @param applicationState Current application state.
     * @returns Boolean value indicating if the submit to PSCA button should be displayed.
     */
    needsSubmitToPscaMenu(
        stepLink: StepLink,
        applicationState?: ApplicationState | null,
    ): boolean {
        return (
            applicationState != null &&
            applicationState.applicationPurpose != null &&
            RouteConstants.Application.isDirectEntryRoute(stepLink.url) &&
            this.userStateService.getRole() !== UserRole.Customer &&
            applicationState.applicationType ===
                ApplicationTypeEnum.PublicSafety &&
            PublicSafetyApplicationPurposeCodes.has(
                applicationState.applicationPurpose,
            )
        );
    }

    /**
     * If the Submit to PSCA menu should be enabled.
     * @param applicationState Current application state.
     * @returns Boolean value indicating if the submit to PSCA menu should be enabled.
     */
    isSubmitToPscaMenuEnabled(
        applicationState?: ApplicationState | null,
    ): boolean {
        return (
            applicationState != null &&
            applicationState.applicationPurpose != null &&
            applicationState.coordinationStatus === null &&
            applicationState.publicSafetyCoordinatorCode ===
                PublicSafetyCoordinatorCodeEnum.PSCA &&
            applicationState.filingStatus != null &&
            !applicationState.filingStatus.startsWith(
                ApplicationFilingStatusEnum.submittedToPsca,
            ) &&
            PublicSafetyApplicationPurposeCodes.has(
                applicationState.applicationPurpose,
            )
        );
    }

    /**
     * If the 5 Day Hold menu should be enabled for an application. This menu should be hidden for applications that are in any of the 800 MHz Hold filing statuses.
     * @param applicationState Current application state.
     * @returns Boolean value indicating if the 5-Day Hold menu should be enabled.
     */
    isSubmitToFiveDayHoldMenuEnabled(
        applicationState?: ApplicationState | null,
    ): boolean {
        return (
            applicationState != null &&
            applicationState.filingStatus != null &&
            !ApplicationFilingStatus800MHzHoldPattern.test(
                applicationState.filingStatus,
            )
        );
    }

    /**
     * If the current step needs the Go Confirm Technical Data Button.
     * @param stepLink Current step link.
     * @returns Boolean value indicating if the Go Confirm Technical Data button should be displayed.
     */
    needsGoConfirmTechnicalDataButton(stepLink: StepLink): boolean {
        return RouteConstants.Go.isConfirmParametersRoute(stepLink.url);
    }

    /**
     * If the current step needs the Go Confirm Order button displayed.
     * @param stepLink Current step link.
     * @return Boolean value indicating if the Go Confirm Order button should be displayed.
     */
    needsGoConfirmOrderButton(stepLink: StepLink): boolean {
        return RouteConstants.Go.isFrequencyResultsRoute(stepLink.url);
    }

    /**
     * If the current step needs the Call EWA for Assistance button displayed
     * @param stepLink Current step link.
     * @returns Boolean value indicating if the Call EWA for Assistance button should be displayed
     */
    needsCallEWAForAssistanceButton(stepLink: StepLink): boolean {
        return (
            RouteConstants.Go.isParametersRoute(stepLink.url) ||
            RouteConstants.Go.isConfirmParametersRoute(stepLink.url) ||
            RouteConstants.Go.isSearchProcessingRoute(stepLink.url) ||
            RouteConstants.Go.isFrequencyResultsRoute(stepLink.url)
        );
    }
}
